<template>
    <UIGridContainer class="service-page">
        <FrontChat />
        <SeoStructuredDataContent component="SeoStructuredDataFaq" :data="structuredData"></SeoStructuredDataContent>
        <UIGridRow>
            <UIGridCol cols="12" md="10" offset-md="1">
                <ContentTitle :content="title" class="text-left" />
                <ServiceNotification v-if="hasNotifications" class="mb-3" :notifications="notifications" />

                <div class="hot-topics pt-1">
                    <h2 class="mt-4">{{ translate('support.hotTopic') }}</h2>

                    <div v-if="hotTopics.length" class="faq">
                        <UIAccordion class="faq-accordion" :items="hotTopics" @click="emitEvent">
                            <template #header="{ item }">
                                {{ item.translations[0]?.title }}
                            </template>
                            <template #default="{ item }">
                                <div v-html="translateGlobalParams(item.translations[0]?.answer)"></div>
                            </template>
                        </UIAccordion>
                    </div>
                </div>

                <div class="categories">
                    <ContentText tag="div" :content="content" />
                    <SupportFAQCategories :categories="categories"></SupportFAQCategories>
                </div>

                <OrderHistoryForm :styling="true" class="mt-5" />

                <div class="contact">
                    <h2>{{ translate('support.contactCS') }}</h2>
                    <div class="mb-4" v-html="translate('support.descriptionCS')"></div>

                    <UIGridRow gutter="small">
                        <UIGridCol md="4">
                            <nuxt-resource
                                src="images/support/support-image-desktop-01.png"
                                class="w-100 mb-3 d-none d-md-block"
                                alt="Customer Service Employee"
                            ></nuxt-resource>
                        </UIGridCol>
                        <UIGridCol md="8">
                            <ServiceLinkWrapper
                                :full-width="true"
                                :show-description="false"
                                :show-notifications="false"
                            />
                        </UIGridCol>
                    </UIGridRow>
                </div>
            </UIGridCol>
        </UIGridRow>
    </UIGridContainer>
</template>

<script>
import { UIAccordion, UIGridCol, UIGridContainer, UIGridRow } from '@dundle/ui/components';
import { ContentText, ContentTitle } from '~/components/content';
import { SupportFAQCategories, FrontChat } from '~/components/support';
import { OrderHistoryForm } from '~/components/order';
import SeoStructuredDataContent from '~/components/seo/SeoStructuredDataContent';
import { ServiceLinkWrapper, ServiceNotification } from '~/components/service';
import BreadcrumbMixin from '~/mixins/BreadcrumbMixin';
import PageMixin from '~/mixins/PageMixin';
import TranslationMixin from '~/mixins/TranslationMixin';

export default {
    name: 'Support',

    meta: {
        topFooter: 'hidden',
    },

    components: {
        FrontChat,
        SeoStructuredDataContent,
        ServiceLinkWrapper,
        ServiceNotification,
        SupportFAQCategories,
        UIGridCol,
        UIGridRow,
        UIGridContainer,
        UIAccordion,
        ContentText,
        ContentTitle,
        OrderHistoryForm,
    },

    mixins: [TranslationMixin, PageMixin, BreadcrumbMixin],

    async asyncData({ app, route, context }) {
        const [pageData, notifications, categories, hotTopics] = await Promise.all([
            PageMixin.fetchData({ app, route }),
            app.$api.notifications.getNotifications(),
            app.$api.faq.getCategories(),
            app.$api.faq.getHotTopics(),
        ]);

        return {
            ...pageData,
            notifications,
            categories,
            hotTopics,
        };
    },

    head() {
        return {
            title: this.translateParams(this.metaTitle),

            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.translateParams(this.metaDescription),
                },
                {
                    hid: 'robots',
                    name: 'robots',
                    content: this.$locale.isTarget() ? 'index, follow' : 'noindex, nofollow',
                },
            ],
        };
    },

    computed: {
        hasNotifications() {
            return this.notifications.length;
        },
        structuredData() {
            const mapped = this.hotTopics.map(hotTopic => {
                return {
                    name: hotTopic.translations[0]?.title,
                    answer: hotTopic.translations[0]?.answer,
                };
            });
            return mapped;
        },
    },

    breadcrumb({ app }, data) {
        return [
            {
                name: app.$locale.regio.toLowerCase(),
                link: app.$locale.path('index'),
                url: 'https://' + app.$locale.domain + app.$locale.path('index'),
            },
            {
                name: 'Support',
                link: app.$locale.path('support'),
                url: 'https://' + app.$locale.domain + app.$locale.path('support'),
            },
        ];
    },

    mounted() {
        this.$gtm?.push({ event: 'page_view_support' });
    },

    methods: {
        emitEvent(event) {
            this.$gtm?.push({ event: 'event_view_support_topic', value: event.item.name });
        },
    },
    httpCaching: {
        maxAge: 60 * 30,
    },

    pageCaching: {
        maxAge: 60 * 30,
    },
};
</script>

<style lang="scss" scoped>
.service-page {
    ::v-deep h2 {
        margin-top: 4rem;
        margin-bottom: 1rem;
    }
}
.faq-accordion {
    ::v-deep .content p {
        margin-bottom: 0;
    }
}

// override for UIRow default gutter in UIRow small gutter
.small-gutter .ui-col {
    ::v-deep .default-gutter .ui-col,
    ::v-deep .normal-gutter .ui-col {
        padding-right: 1rem !important;
        padding-left: 1rem !important;
    }
}
</style>
<i18n src="@dundle/locale/data/support.json"></i18n>
